"use strict";
//
// Copyright © 2020, 2021 Anticrm Platform Contributors.
// Copyright © 2021 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.addLocation = addLocation;
exports.getPlugins = getPlugins;
exports.getResource = getResource;
exports.getResourceP = getResourceP;
exports.getResourceC = getResourceC;
exports.getResourcePlugin = getResourcePlugin;
const event_1 = require("./event");
const ident_1 = require("./ident");
const status_1 = require("./status");
const metadata_1 = require("./metadata");
const platform_1 = __importDefault(require("./platform"));
const locations = new Map();
/**
 * @public
 * @param plugin -
 * @param module -
 */
function addLocation(plugin, module) {
    locations.set(plugin, module);
}
/**
 * @public
 * return list of registred plugins.
 */
function getPlugins() {
    return Array.from(locations.keys());
}
function getLocation(plugin) {
    const location = locations.get(plugin);
    if (location === undefined) {
        throw new status_1.PlatformError(new status_1.Status(status_1.Severity.ERROR, platform_1.default.status.NoLocationForPlugin, {
            plugin
        }));
    }
    return location;
}
const loading = new Map();
function loadPlugin(id) {
    let pluginLoader = loading.get(id);
    if (pluginLoader === undefined) {
        const status = new status_1.Status(status_1.Severity.INFO, platform_1.default.status.LoadingPlugin, {
            plugin: id
        });
        const loadHelper = (0, metadata_1.getMetadata)(platform_1.default.metadata.LoadHelper);
        const locationLoader = getLocation(id);
        pluginLoader = (0, event_1.monitor)(status, loadHelper !== undefined ? loadHelper(locationLoader) : locationLoader()).then(async (plugin) => {
            try {
                // In case of ts-node, we have a bit different import structure, so let's check for it.
                if (typeof plugin.default === 'object') {
                    // eslint-disable-next-line @typescript-eslint/return-await
                    return await plugin.default.default();
                }
                return await plugin.default();
            }
            catch (err) {
                console.error('failed to load ', id, err);
                throw err;
            }
        });
        loading.set(id, pluginLoader);
    }
    return pluginLoader;
}
const cachedResource = new Map();
/**
 * @public
 * @param resource -
 * @returns
 */
async function getResource(resource) {
    const cached = cachedResource.get(resource);
    if (cached !== undefined) {
        return cached;
    }
    const info = (0, ident_1._parseId)(resource);
    let resources = loading.get(info.component) ?? loadPlugin(info.component);
    if (resources instanceof Promise) {
        resources = await resources;
        loading.set(info.component, resources);
    }
    const value = resources[info.kind]?.[info.name];
    if (value === undefined) {
        throw new status_1.PlatformError(new status_1.Status(status_1.Severity.ERROR, platform_1.default.status.ResourceNotFound, { resource }));
    }
    cachedResource.set(resource, value);
    return value;
}
/**
 * @public
 * @param resource -
 * @returns
 */
function getResourceP(resource) {
    return cachedResource.get(resource) ?? getResource(resource);
}
/**
 * @public
 * @param resource -
 * @returns
 */
function getResourceC(resource, callback) {
    if (resource === undefined) {
        callback(undefined);
        return;
    }
    const cached = cachedResource.get(resource);
    if (cached !== undefined) {
        callback(cached);
    }
    else {
        void getResource(resource)
            .then((r) => {
            callback(r);
        })
            .catch(() => {
            callback(undefined);
        });
    }
}
/**
 * @public
 */
function getResourcePlugin(resource) {
    const info = (0, ident_1._parseId)(resource);
    return info.component;
}
//# sourceMappingURL=resource.js.map